/**
 * Resource Alerts — display helpers: icons, labels, time, value formatting,
 * and actor-name extraction (acknowledged_by / updated_by can be a string
 * or an {id,name,email} object).
 */
import type { AlertType, Severity, Actor } from "./api/resourceAlerts.schemas";

export const ALERT_TYPE_LABEL: Record<AlertType, string> = {
  cpu:              "CPU",
  memory:           "Memory",
  disk_io:          "Disk I/O",
  zombie_processes: "Zombie Processes",
  load_average:     "Load Average",
};

export const ALERT_TYPE_ICON: Record<AlertType, string> = {
  cpu:              "fa-microchip",
  memory:           "fa-memory",
  disk_io:          "fa-hard-drive",
  zombie_processes: "fa-ghost",
  load_average:     "fa-gauge-high",
};

export const SEVERITY_ICON: Record<Severity, string> = {
  critical: "fa-circle-exclamation",
  high:     "fa-triangle-exclamation",
  medium:   "fa-circle-info",
  low:      "fa-circle-dot",
  info:     "fa-circle-dot",
};

/** Severity ordering for sorting (critical first). */
export const SEVERITY_RANK: Record<Severity, number> = {
  critical: 0, high: 1, medium: 2, low: 3, info: 4,
};

/** Actor → display name, from either a string or {name,email,id}. */
export function actorName(a: Actor): string {
  if (!a) return "—";
  if (typeof a === "string") return a;
  return a.name ?? a.email ?? a.id ?? "—";
}

/** Compact "N ago" from a UTC ISO timestamp. */
export function timeAgo(iso: string | null | undefined): string {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "—";
  const s = Math.max(0, (Date.now() - t) / 1000);
  if (s < 60)     return `${Math.floor(s)}s ago`;
  if (s < 3600)   return `${Math.floor(s / 60)}m ago`;
  if (s < 86_400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86_400)}d ago`;
}

export function fmtAbsolute(iso: string | null | undefined): string {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  return new Date(t).toLocaleString();
}

/** Format a metric value with its unit, e.g. 93 + "%" → "93%". */
export function fmtValue(value: number | null | undefined, unit: string | null | undefined, decimals = 1): string {
  if (value === null || value === undefined) return "—";
  const n = Number.isInteger(value) ? value.toFixed(0) : value.toFixed(decimals);
  return `${n}${unit ?? ""}`;
}
